import { useLocalStorage } from './useLocalStorage';
import { useMemo, useCallback } from 'react';
import type { AllSiteClickData } from '../types';

// 获取一天的开始时间（午夜）
const getStartOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

// 获取一周的开始时间（周一）
const getStartOfWeek = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // 如果是周日，则-6，否则+1
  const startOfWeek = new Date(d.setDate(diff));
  startOfWeek.setHours(0, 0, 0, 0);
  return startOfWeek;
};

// 获取一个月的开始时间
const getStartOfMonth = (date: Date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), 1);
};


export function useSiteClicks() {
  const [clickData, setClickData] = useLocalStorage<AllSiteClickData>('site-clicks', {});

  const timeStamps = useMemo(() => {
    const now = new Date();
    return {
      daily: getStartOfDay(now).getTime(),
      weekly: getStartOfWeek(now).getTime(),
      monthly: getStartOfMonth(now).getTime(),
    };
  }, []);

  const updatePeriodCount = (data: any, period: 'daily' | 'weekly' | 'monthly', timestamp: number) => {
    const lastResetKey = `last${period.charAt(0).toUpperCase() + period.slice(1)}Reset` as const;
    if (data[lastResetKey] < timestamp) {
      data[period] = 1;
      data[lastResetKey] = timestamp;
    } else {
      data[period] += 1;
    }
  };

  const trackClick = useCallback((siteId: string) => {
    setClickData(prevData => {
      const currentSiteData = prevData[siteId] || {
        daily: 0,
        weekly: 0,
        monthly: 0,
        lastDailyReset: 0,
        lastWeeklyReset: 0,
        lastMonthlyReset: 0,
      };

      const newData = { ...currentSiteData };
      updatePeriodCount(newData, 'daily', timeStamps.daily);
      updatePeriodCount(newData, 'weekly', timeStamps.weekly);
      updatePeriodCount(newData, 'monthly', timeStamps.monthly);

      return {
        ...prevData,
        [siteId]: newData
      };
    });
  }, [timeStamps]);

  const removeClickData = useCallback((siteId: string) => {
    setClickData(prevData => {
      const { [siteId]: removed, ...newData } = prevData;
      return newData;
    });
  }, [setClickData]);

  return { clickData, trackClick, removeClickData };
}